import { Card, CardBody, CardSubtitle, CardTitle } from "@/components/Card";
import { ArrowRight, CalendarClock, Dot, GraduationCap } from "lucide-react";
import { useNavigate } from "react-router";
import ReportStatusBanner from "./ReportStatusBanner";
import { formatLessons } from "@cle/utils/lessonLimits";

export interface PendingReportingPeriodItem {
  id: string;
  kind: "injunction" | "imposition" | "reporting-period";
  title: string;
  periodRange: string;
  requiredLessons: number;
  transferredLessons: number;
  reportedLessons: number;
  /** False når CLE ikke kunne svare — vis "ukendt" frem for et misvisende 0. */
  reportedLessonsKnown?: boolean;
  documentationDeadline: string;
  reportingDeadline: string;
  ctaLabel?: string;
  to?: string;
}

type Props = {
  item: PendingReportingPeriodItem;
};

const PendingReportCard = ({ item }: Props) => {
  const navigate = useNavigate();
  const headingId = `cle-afventende-${item.kind}-${item.id}-title`;

  const handleReportClick = () => {
    if (!item.to) {
      return;
    }

    void navigate(item.to);
  };

  return (
    <Card
      className="cle-afventende-indberetninger-card cle-afventende-indberetninger-card--compact border border-white "
      role="group"
      aria-labelledby={headingId}
    >
      <CardBody className="cle-afventende-indberetninger-card__inner flex-column d-flex gap-2 p-0 relative">
        <div
          className={`position-absolute  top-0 start-0 cle-bg-destructive`}
          style={{
            width: "6px",
            height: "100%",
            borderRadius: "0.5rem 0 0 0.5rem",
          }}
        ></div>
        <div className="cle-afventende-indberetninger-card__header-row d-flex flex-wrap align-items-start justify-content-between gap-2">
          <div className="min-w-0">
            {item.kind === "reporting-period" && (
              <>
                {" "}
                <CardTitle
                  as="h3"
                  id={headingId}
                  className="cle-afventende-indberetninger-card__subtitle mb-1"
                >
                  {item.title}
                </CardTitle>
                {item.periodRange ? (
                  <CardSubtitle className="cle-afventende-indberetninger-card__title mb-0 mt-1">
                    {item.periodRange}
                  </CardSubtitle>
                ) : null}
              </>
            )}
            {(item.kind === "injunction" || item.kind === "imposition") && (
              <>
                <CardTitle
                  as="h3"
                  id={headingId}
                  className="cle-afventende-indberetninger-card__title mb-0 fs-3"
                >
                  {item.title}
                </CardTitle>
                {item.periodRange ? (
                  <CardSubtitle className="cle-afventende-indberetninger-card__subtitle mb-0 mt-1">
                    Dokumentationsperiode: {item.periodRange}
                  </CardSubtitle>
                ) : null}
              </>
            )}
          </div>
          {item.to ? (
            <button
              type="button"
              id={`cle-indberet-rapport-${item.id}`}
              className="btn btn-dark btn-sm cle-afventende-indberetninger-card__cta d-flex align-items-center gap-2 flex-shrink-0 rounded-3 fw-semibold"
              onClick={handleReportClick}
            >
              {item.ctaLabel ?? "Indberet"}
              <ArrowRight size={17} strokeWidth={2} />
            </button>
          ) : null}
        </div>

        <ReportStatusBanner
          id={`cle-report-status-banner-${item.id}`}
          reportedLessons={item.reportedLessons}
          requiredLessons={item.requiredLessons}
          transferredLessons={item.transferredLessons}
        />

        <div className="d-flex align-items-center gap-2">
          <div className="d-flex gap-2 align-items-center fs-6 ">
            <GraduationCap size={17} strokeWidth={2} className="" />
            <p className="mb-0">
              Godkendte lektioner:{" "}
              <span className="fw-bold">
                {item.reportedLessonsKnown === false
                  ? "kunne ikke hentes"
                  : `${formatLessons(item.reportedLessons)}/${formatLessons(
                      item.requiredLessons,
                    )}`}
              </span>
            </p>
          </div>
          <Dot size={17} strokeWidth={2} className="mt-" />
          <div className="d-flex gap-2 align-items-center fs-6">
            <CalendarClock size={17} strokeWidth={2} className="" />
            <p className="mb-0">
              Indberetningsfrist:{" "}
              <span className="fw-bold">{item.reportingDeadline}</span>
            </p>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

export default PendingReportCard;
